import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { EmpleadoViewModel } from "src/app/_core/models/centros/empleado.model";
import {
	empleadoInitials,
	empleadoLabel,
} from "src/app/_core/utils/empleado-display";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

/**
 * Selector de personas reutilizable (forms). Es `ControlValueAccessor`
 * (formControlName) y carga sus propios empleados (`MasterService`),
 * permitiendo preselección por id. La presentación (input + desplegable `.ds`,
 * filtrado, teclado) la delega en `app-ds-autocomplete`.
 */
@Component({
	selector: "app-empleado-autocomplete",
	templateUrl: "./empleado-autocomplete.component.html",
	styleUrls: ["./empleado-autocomplete.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => EmpleadoAutocompleteComponent),
			multi: true,
		},
	],
	standalone: false,
})
export class EmpleadoAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Empleado";
	@Input() placeholder = "Buscar por nombre, email o código…";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint = "";

	// Outputs
	@Output() empleadoSelected = new EventEmitter<EmpleadoViewModel>();

	// State
	empleados: EmpleadoViewModel[] = [];
	empleadoId: number | null = null;
	selectedEmpleado: EmpleadoViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadEmpleados();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.empleadoId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de empleados desde la API
	 */
	private loadEmpleados(): void {
		this.masterService
			.getEmpleados()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (empleados) => {
					this.empleados = empleados ?? [];
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los empleados",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedEmpleado =
			this.empleadoId != null
				? (this.empleados.find((e) => e.id === this.empleadoId) ?? null)
				: null;
	}

	/**
	 * Función de display para el autocomplete (texto del input tras seleccionar)
	 */
	displayEmpleado = (empleado: EmpleadoViewModel | string): string => {
		if (typeof empleado === "string") {
			return empleado;
		}
		// Fallback coherente con el visor: nombre → email → código → #id.
		return empleadoLabel(empleado);
	};

	/**
	 * Búsqueda multi-campo (nombre, email, código, función), insensible a
	 * acentos/mayúsculas. `query` llega ya en minúsculas desde el control.
	 */
	filterEmpleado = (empleado: EmpleadoViewModel, query: string): boolean => {
		const norm = (s?: string | null): string =>
			(s ?? "")
				.normalize("NFD")
				.replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
				.toLowerCase();
		const q = norm(query);
		return (
			norm(empleado?.nombre).includes(q) ||
			norm(empleado?.email).includes(q) ||
			norm(empleado?.codigo).includes(q) ||
			norm(empleado?.funcionAgregada).includes(q)
		);
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(empleado: EmpleadoViewModel | null): void {
		this.empleadoId = empleado ? empleado.id : null;
		this.selectedEmpleado = empleado;
		this.onChange(this.empleadoId);
		this.onTouched();
		if (empleado) {
			this.empleadoSelected.emit(empleado);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}

	/** Iniciales para el avatar de la fila del desplegable. */
	initials(empleado: EmpleadoViewModel): string {
		return empleadoInitials(empleado);
	}
}
